import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, jsonBody } from "../../api/client";
import type { Booking } from "../../api/types";
import { useVenueScope } from "../../app/venueScope";
import { formatDateTime, titleCase, zonedDateTimeInput, zonedInputToISO } from "../../lib/format";
import {
  Card,
  CardHeader,
  EditorPage,
  ErrorPanel,
  Field,
  FormActions,
  Notice,
  StatusPill,
} from "../../ui/components";
import { DetailPageSkeleton } from "../../ui/skeletons";
import { useAllLocations, useVenues } from "../venues/queries";
import type { RescheduleProposal } from "./calendarTypes";

interface RescheduleDraft {
  occurrenceId: string;
  scope: "single_occurrence" | "following" | "series" | "booking";
  start: string;
  end: string;
  locationIds: string[];
  reason: string;
  baseVersion: number;
}

export function ReschedulePage() {
  const { bookingId = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const booking = useQuery({
    queryKey: ["bookings", bookingId],
    queryFn: () => api<Booking>(`/api/v1/bookings/${bookingId}`),
    enabled: Boolean(bookingId),
  });
  // Rescheduling offers spaces in the venue selected in the top bar, the same single-venue
  // scope every other booking screen follows.
  const { selectedVenueId, loading: venueScopeLoading } = useVenueScope();
  const venuesQuery = useVenues();
  const venues = useMemo(
    () => (venuesQuery.data?.items ?? []).filter((venue) => venue.id === selectedVenueId),
    [venuesQuery.data, selectedVenueId],
  );
  const locationsQuery = useAllLocations(venues);
  const [proposal, setProposal] = useState<RescheduleProposal | null>(null);
  const [draft, setDraft] = useState<RescheduleDraft | null>(null);
  const [overrideConfirmed, setOverrideConfirmed] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  const activeLocations = useMemo(
    () =>
      locationsQuery.locations.filter(
        (location) => location.status === "active" && location.isBookable,
      ),
    [locationsQuery.locations],
  );

  async function createProposal(
    value: RescheduleDraft,
    confirmConflictOverride = false,
    conflictOverrideReason = "",
  ) {
    setWorking(true);
    setError(undefined);
    try {
      const created = await api<RescheduleProposal>(
        `/api/v1/bookings/${bookingId}/reschedule-proposals`,
        {
          method: "POST",
          body: jsonBody({
            ...value,
            confirmConflictOverride,
            conflictOverrideReason,
          }),
        },
      );
      setProposal(created);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function propose(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!booking.data) return;
    const form = new FormData(event.currentTarget);
    const occurrenceId = String(form.get("occurrenceId") ?? "");
    const occurrence = booking.data.occurrences?.find((item) => item.id === occurrenceId);
    if (!occurrence) {
      setError(new Error("Select a booking occurrence."));
      return;
    }
    const locationIds = form.getAll("locationIds").map(String);
    if (locationIds.length === 0) {
      setError(new Error("Select at least one venue location."));
      return;
    }
    try {
      const value: RescheduleDraft = {
        occurrenceId,
        scope: String(form.get("scope")) as RescheduleDraft["scope"],
        start: zonedInputToISO(String(form.get("start")), booking.data.timezone),
        end: zonedInputToISO(String(form.get("end")), booking.data.timezone),
        locationIds,
        reason: String(form.get("reason") ?? ""),
        baseVersion: booking.data.version,
      };
      setDraft(value);
      setOverrideConfirmed(false);
      setOverrideReason("");
      await createProposal(value);
    } catch (caught) {
      setError(caught);
    }
  }

  async function approveOverride() {
    if (!draft || !overrideConfirmed || overrideReason.trim().length < 10) return;
    await createProposal(draft, true, overrideReason.trim());
  }

  async function applyProposal() {
    if (!proposal) return;
    setWorking(true);
    setError(undefined);
    try {
      await api<Booking>(
        `/api/v1/bookings/${bookingId}/reschedule-proposals/${proposal.id}/apply`,
        { method: "POST" },
      );
      await queryClient.invalidateQueries({ queryKey: ["bookings"] });
      await queryClient.invalidateQueries({ queryKey: ["calendar"] });
      navigate(`/bookings/${bookingId}`, { replace: true });
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (booking.isLoading || venueScopeLoading || venuesQuery.isLoading || locationsQuery.loading) {
    return (
      <DetailPageSkeleton
        title="Reschedule"
        backTo={`/bookings/${bookingId}`}
        backLabel="Back to booking"
      />
    );
  }
  if (booking.error || venuesQuery.error || locationsQuery.error || !booking.data) {
    return <ErrorPanel error={booking.error ?? venuesQuery.error ?? locationsQuery.error} />;
  }

  const initialOccurrence = booking.data.occurrences?.[0];
  const initialLocationIds =
    initialOccurrence?.allocations.map((allocation) => allocation.locationId) ?? [];

  return (
    <EditorPage
      title={`Reschedule ${booking.data.reference}`}
      description="Preview the full impact first. Nothing changes until you explicitly apply a proposal."
      backTo={`/bookings/${bookingId}`}
      backLabel="Back to booking"
    >
      {error ? <ErrorPanel error={error} /> : null}
      <Card>
        <CardHeader
          title={booking.data.title}
          description={`${booking.data.customerName} · ${booking.data.timezone}`}
          actions={<StatusPill value={booking.data.status} />}
        />
        <dl className="definition-list">
          <dt>Current start</dt>
          <dd>{formatDateTime(booking.data.start, booking.data.timezone)}</dd>
          <dt>Current end</dt>
          <dd>{formatDateTime(booking.data.end, booking.data.timezone)}</dd>
          <dt>Occurrences</dt>
          <dd>{booking.data.occurrences?.length ?? 0}</dd>
        </dl>
      </Card>

      {!proposal ? (
        <Card>
          <form className="form-stack" onSubmit={propose}>
            <CardHeader
              title="Propose a schedule change"
              description="The selected anchor occurrence determines how following and series changes are shifted."
            />
            <div className="form-grid">
              <Field label="Anchor occurrence">
                <select name="occurrenceId" required defaultValue={initialOccurrence?.id}>
                  {booking.data.occurrences?.map((occurrence) => (
                    <option key={occurrence.id} value={occurrence.id}>
                      {formatDateTime(occurrence.start, booking.data?.timezone)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Change scope">
                <select name="scope" defaultValue="single_occurrence">
                  <option value="single_occurrence">Only this occurrence</option>
                  <option value="following">This and following occurrences</option>
                  <option value="series">Entire series</option>
                  <option value="booking">Entire booking</option>
                </select>
              </Field>
              <Field label={`New start (${booking.data.timezone})`}>
                <input
                  name="start"
                  type="datetime-local"
                  required
                  defaultValue={zonedDateTimeInput(initialOccurrence?.start, booking.data.timezone)}
                />
              </Field>
              <Field label={`New end (${booking.data.timezone})`}>
                <input
                  name="end"
                  type="datetime-local"
                  required
                  defaultValue={zonedDateTimeInput(initialOccurrence?.end, booking.data.timezone)}
                />
              </Field>
              <fieldset className="field full">
                <legend>Locations</legend>
                <div className="choice-grid">
                  {venues.map((venue) => (
                    <div className="choice-group" key={venue.id}>
                      <strong>{venue.name}</strong>
                      {activeLocations
                        .filter((location) => location.venueId === venue.id)
                        .map((location) => (
                          <label className="check-field" key={location.id}>
                            <input
                              type="checkbox"
                              name="locationIds"
                              value={location.id}
                              defaultChecked={initialLocationIds.includes(location.id)}
                            />
                            <span>{location.name}</span>
                          </label>
                        ))}
                    </div>
                  ))}
                </div>
              </fieldset>
              <div className="full">
                <Field label="Reason for change" hint="Required for the immutable audit history.">
                  <textarea name="reason" required minLength={3} maxLength={1000} />
                </Field>
              </div>
            </div>
            <FormActions
              submitting={working}
              submitLabel="Preview schedule change"
              onCancel={() => navigate(`/bookings/${bookingId}`)}
            />
          </form>
        </Card>
      ) : (
        <Card>
          <div className="stack">
            <CardHeader
              title="Review proposal"
              description={`${titleCase(proposal.scope)} · ${proposal.state.occurrences.length} occurrence${proposal.state.occurrences.length === 1 ? "" : "s"}`}
              actions={<StatusPill value={proposal.status} />}
            />
            <Notice
              tone={proposal.conflicts.length ? "danger" : "success"}
              title={
                proposal.conflicts.length ? "Conflicts require attention" : "No conflicts found"
              }
            >
              {proposal.conflicts.length
                ? "Review every overlap. Applying an override requires explicit authority, acknowledgement, and a reason."
                : "The schedule can be applied at the current booking version."}
            </Notice>
            <div className="proposal-summary">
              {proposal.state.occurrences.map((occurrence) => (
                <div className="card" key={occurrence.id}>
                  <strong>{formatDateTime(occurrence.start, booking.data?.timezone)}</strong>
                  <span> to {formatDateTime(occurrence.end, booking.data?.timezone)}</span>
                </div>
              ))}
            </div>
            {proposal.conflicts.map((conflict) => (
              <div className="card" key={conflict.allocationId}>
                <strong>
                  {conflict.bookingReference} · {conflict.bookingTitle}
                </strong>
                <p>
                  {conflict.locationName}
                  <br />
                  {formatDateTime(conflict.start)}–{formatDateTime(conflict.end)}
                </p>
              </div>
            ))}
            {proposal.conflicts.length && !proposal.state.confirmConflictOverride ? (
              proposal.canOverride ? (
                <div className="stack">
                  <label className="check-field">
                    <input
                      type="checkbox"
                      checked={overrideConfirmed}
                      onChange={(event) => setOverrideConfirmed(event.target.checked)}
                    />
                    <span>
                      I understand this creates an intentional conflict and will be audited.
                    </span>
                  </label>
                  <Field label="Required override reason" hint="At least 10 characters.">
                    <textarea
                      value={overrideReason}
                      onChange={(event) => setOverrideReason(event.target.value)}
                      minLength={10}
                      maxLength={1000}
                    />
                  </Field>
                  <button
                    className="button danger"
                    type="button"
                    disabled={working || !overrideConfirmed || overrideReason.trim().length < 10}
                    onClick={approveOverride}
                  >
                    Create authorized override proposal
                  </button>
                </div>
              ) : (
                <Notice tone="warning">
                  Your role cannot override conflicts. Return to the schedule form or ask an
                  authorized colleague.
                </Notice>
              )
            ) : null}
            <div className="form-actions">
              <button
                className="button secondary"
                type="button"
                disabled={working}
                onClick={() => setProposal(null)}
              >
                Change proposal
              </button>
              <button
                className="button primary"
                type="button"
                disabled={
                  working ||
                  (proposal.conflicts.length > 0 && !proposal.state.confirmConflictOverride)
                }
                onClick={applyProposal}
              >
                Apply schedule change
              </button>
            </div>
          </div>
        </Card>
      )}
    </EditorPage>
  );
}
